import {
  EntityNamePickerFieldExtension,
  EntityPickerFieldExtension,
  EntityTagsPickerFieldExtension,
  MultiEntityPickerFieldExtension,
  MyGroupsPickerFieldExtension,
  OwnedEntityPickerFieldExtension,
  OwnerPickerFieldExtension,
  RepoBranchPickerFieldExtension,
  RepoOwnerPickerFieldExtension,
  RepoUrlPickerFieldExtension,
  ScaffolderFieldExtensions,
  createScaffolderFieldExtension,
  scaffolderPlugin,
} from '@backstage/plugin-scaffolder';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder';
import {
  ScaffolderField,
  SecretWidget,
} from '@backstage/plugin-scaffolder-react/alpha';
import type { ComponentProps } from 'react';
import { PlacementPicker } from './PlacementPicker';
import { ControlIdToken } from './ControlIdToken';
import { GettingStarted } from './GettingStarted';
import { OrgRepoPicker } from './OrgRepoPicker';
import { ServiceOptionsField } from './ServiceOptionsField';
import { ServiceNameField } from './ServiceNameField';
import { ChoiceCardsField } from './ChoiceCardsField';

/**
 * The field extensions a template can name in `ui:field`.
 *
 * The scaffolder ships these as DEFAULT_SCAFFOLDER_FIELD_EXTENSIONS, which is
 * internal to the plugin — its stock Templates sub-page passes them to the
 * wizard for us. We replace that sub-page (see ./index.tsx), so the set has to
 * be rebuilt here from the plugin's public exports. Every picker below is a
 * public export of @backstage/plugin-scaffolder; only Secret is reconstructed,
 * for the reason given on it.
 *
 * Keep this list in step with the plugin on upgrade: a template naming a field
 * that is missing here falls back to a plain text input rather than failing
 * loudly.
 */

/**
 * `Secret` has no public export, and leaving it out is not harmless: without
 * it, a template's `ui:field: Secret` degrades to a plain text input, and the
 * value is submitted as an ordinary parameter — recorded in the task and
 * visible in its logs — instead of travelling in the secrets channel.
 *
 * SecretWidget owns all of the behaviour (masking the form value, holding the
 * real one, writing it to the secrets context); this only supplies the
 * surrounding label/description/error furniture, exactly as the plugin's own
 * SecretInput does. Do not pass an onChange through — SecretWidget calls it
 * with the mask, so intercepting it stores asterisks as the secret.
 *
 * The plugin also has a Backstage UI variant of this field selected by
 * `EXPERIMENTAL_theme`; we leave that at its 'mui' default, so only this
 * branch is reachable.
 */
const SecretInput = (props: FieldExtensionComponentProps<string>) => {
  const {
    schema: { description },
    rawErrors,
    disabled,
    errors,
    required,
    uiSchema,
  } = props;

  return (
    <ScaffolderField
      rawErrors={rawErrors}
      rawDescription={uiSchema['ui:description'] ?? description}
      disabled={disabled}
      errors={errors}
      required={required}
    >
      {/* Same spread the plugin's SecretInput does. The cast is only needed
          because SecretWidget's published props narrow `errors` to a string
          where the field gives an ErrorSchema; the widget reads neither. */}
      <SecretWidget
        {...(props as unknown as ComponentProps<typeof SecretWidget>)}
      />
    </ScaffolderField>
  );
};

const SecretFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'Secret',
    component: SecretInput,
  }),
);

/**
 * Ours, not the plugin's: resolves WHERE a new thing lands by asking
 * launchpad's placement policy, and collects the Google ID token the
 * `control:*` actions act with. See ./PlacementPicker.
 */
const PlacementFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'PlacementPicker',
    component: PlacementPicker,
  }),
);

/**
 * Collects the caller's Google ID token and nothing else, for templates that
 * call a `control:*` action but have no placement step. See ./ControlIdToken —
 * two of the three golden paths failed on step 0 without it.
 */
const ControlIdTokenFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ControlIdToken',
    component: ControlIdToken,
  }),
);

/**
 * The first step of the create flow: what to do before the portal can help.
 * Not a form field — it collects nothing and satisfies itself on mount. See
 * ./GettingStarted for why it is a wall of text on purpose.
 */
const GettingStartedFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GettingStarted',
    component: GettingStarted,
  }),
);

/**
 * Pick a repository that already EXISTS in the org, and see what it is missing.
 * Replaces the free-text repository field, which is how a pasted browser URL
 * was once submitted as a repository name. See ./OrgRepoPicker.
 */
const OrgRepoPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'OrgRepoPicker',
    component: OrgRepoPicker,
  }),
);

/**
 * Every option a service can ask for, rendered from Control's own published
 * schema rather than from a list in a template. See ./ServiceOptionsField.
 */
const ServiceOptionsFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ServiceOptions',
    component: ServiceOptionsField,
  }),
);

/**
 * The service name, pre-filled from the repository already chosen. See
 * ./ServiceNameField — by this step the portal knows the answer, and asking
 * with an empty box invites an illegal one.
 */
const ServiceNameFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ServiceName',
    component: ServiceNameField,
  }),
);

/**
 * Ours: an enum as selectable cards, matching the request page. See
 * ./ChoiceCardsField. A template must not use it until a portal that registers
 * it is deployed — templates are read from main by URL, so they go live on
 * merge, before the deploy that would teach the form the field.
 */
const ChoiceCardsFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ChoiceCards',
    component: ChoiceCardsField,
  }),
);

/**
 * Rendered, not mounted — useCustomFieldExtensions reads the extension data off
 * these elements without them ever hitting the DOM.
 */
export const scaffolderFieldExtensionsElement = (
  <ScaffolderFieldExtensions>
    <EntityPickerFieldExtension />
    <EntityNamePickerFieldExtension />
    <EntityTagsPickerFieldExtension />
    <RepoUrlPickerFieldExtension />
    <OwnerPickerFieldExtension />
    <OwnedEntityPickerFieldExtension />
    <MyGroupsPickerFieldExtension />
    <MultiEntityPickerFieldExtension />
    <RepoBranchPickerFieldExtension />
    <RepoOwnerPickerFieldExtension />
    <SecretFieldExtension />
    <PlacementFieldExtension />
    <ControlIdTokenFieldExtension />
    <GettingStartedFieldExtension />
    <OrgRepoPickerFieldExtension />
    <ServiceOptionsFieldExtension />
    <ServiceNameFieldExtension />
    <ChoiceCardsFieldExtension />
  </ScaffolderFieldExtensions>
);
